import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  DbOntologyIndex,
  DbOntologyTable,
  useCreateDbOntologyIndex,
  useUpdateDbOntologyNode,
} from "@/service-hooks/use-get-db-ontology-service";

interface DbOntologyIndexDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  dataLakeLabel?: string;
  tables: DbOntologyTable[];
  index?: DbOntologyIndex | null;
  defaultTableId?: string;
  onSaved?: () => void;
  isReadOnly?: boolean;
}

type IndexType = "BTREE" | "BITMAP" | "FUNCTION_BASED" | "DOMAIN";

interface FormState {
  tableId: string;
  name: string;
  columns: string;
  isUnique: boolean;
  indexType: IndexType;
  whereClause: string;
  tablespace: string;
}

const EMPTY: FormState = {
  tableId: "",
  name: "",
  columns: "",
  isUnique: false,
  indexType: "BTREE",
  whereClause: "",
  tablespace: "",
};

const parseColumns = (raw: string): string[] => {
  const text = raw.trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch {
    // fall through
  }
  return text
    .split(/[\n,]/g)
    .map((p) => p.trim())
    .filter(Boolean);
};

export default function DbOntologyIndexDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  dataLakeLabel,
  tables,
  index,
  defaultTableId,
  onSaved,
  isReadOnly: readOnlyProp = false,
}: DbOntologyIndexDialogProps) {
  const isEdit = !!index?.id;
  // Editing an existing index/constraint isn't supported (saving it failed with 400), so it
  // opens view-only; delete and re-create it to change it.
  const isReadOnly = readOnlyProp || isEdit;
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const createIndex = useCreateDbOntologyIndex();
  const updateNode = useUpdateDbOntologyNode();

  useEffect(() => {
    if (!isOpen) return;
    if (index) {
      const it = (index.indexType as IndexType) || "BTREE";
      setForm({
        tableId: index.tableId || "",
        name: index.name || "",
        columns: Array.isArray(index.columns) ? index.columns.join(", ") : "",
        isUnique: !!index.isUnique,
        indexType: ["BTREE", "BITMAP", "FUNCTION_BASED", "DOMAIN"].includes(it)
          ? it
          : "BTREE",
        whereClause: "",
        tablespace: "",
      });
    } else {
      setForm({ ...EMPTY, tableId: defaultTableId || "" });
    }
  }, [isOpen, index, defaultTableId]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.tableId) return;
    const columns = parseColumns(form.columns);
    if (columns.length === 0) return;
    try {
      if (isEdit && index?.id) {
        await updateNode.mutateAsync({
          label: "DDLIndex",
          projectUuid,
          id: index.id,
          dataLakeId,
          data: {
            name: form.name.trim(),
            columns,
            isUnique: form.isUnique,
            indexType: form.indexType,
            ...(form.whereClause.trim() ? { whereClause: form.whereClause.trim() } : {}),
            ...(form.tablespace.trim() ? { tablespace: form.tablespace.trim() } : {}),
          },
        });
      } else {
        await createIndex.mutateAsync({
          projectUuid,
          dataLakeId,
          tableId: form.tableId,
          name: form.name.trim(),
          columns,
          isUnique: form.isUnique,
          indexType: form.indexType,
          whereClause: form.whereClause.trim() || undefined,
          tablespace: form.tablespace.trim() || undefined,
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  const isPending = createIndex.isPending || updateNode.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isReadOnly ? "View Index" : isEdit ? "Edit Index" : "Create Index"}
            {dataLakeLabel ? ` — ${dataLakeLabel}` : ""}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the index fields for the selected table.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Table <span className="text-error">*</span>
            </label>
            <select
              value={form.tableId}
              onChange={(e) => update("tableId", e.target.value)}
              disabled={isReadOnly || isEdit || !!defaultTableId}
              className="w-full h-10 rounded-md border border-input bg-surface text-white px-3 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">Select a table…</option>
              {tables
                .filter((t) => !!t.id)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Enter index name"
              readOnly={isReadOnly}
              className="bg-surface text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Index Type</label>
              <select
                value={form.indexType}
                onChange={(e) => update("indexType", e.target.value as IndexType)}
                disabled={isReadOnly}
                className="w-full h-10 rounded-md border border-input bg-surface text-white px-3 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="BTREE">BTREE</option>
                <option value="BITMAP">BITMAP</option>
                <option value="FUNCTION_BASED">FUNCTION_BASED</option>
                <option value="DOMAIN">DOMAIN</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Tablespace</label>
              <Input
                value={form.tablespace}
                onChange={(e) => update("tablespace", e.target.value)}
                placeholder="Optional"
                readOnly={isReadOnly}
                className="bg-surface text-white"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={form.isUnique}
              onCheckedChange={(checked) => update("isUnique", checked)}
              disabled={isReadOnly}
            />
            <span className="text-sm text-white">Unique</span>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Columns <span className="text-error">*</span>
            </label>
            <Input
              value={form.columns}
              onChange={(e) => update("columns", e.target.value)}
              placeholder='Comma separated, or JSON array (e.g. ["id", "name"])'
              readOnly={isReadOnly}
              className="bg-surface text-white font-mono text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Where Clause</label>
            <Input
              value={form.whereClause}
              onChange={(e) => update("whereClause", e.target.value)}
              placeholder="e.g. status = 'ACTIVE'"
              readOnly={isReadOnly}
              className="bg-surface text-white font-mono text-xs"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button
              onClick={handleSubmit}
              disabled={
                !form.name.trim() ||
                !form.tableId ||
                !form.columns.trim() ||
                isPending
              }
              className="bg-primary hover:bg-primary-dark text-white"
            >
              {isPending ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
